import React, { useState, useEffect, useContext } from "react";
import ReactDOM from 'react-dom';
import toast from "react-hot-toast";
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
    Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { SubscriptionApi } from "../../../api/authority/generated/index.ts";
import { GlobalContext } from "../../../utils/context/GlobalContextProvider.jsx";
import { useTranslation } from "react-i18next";
import PlanName from "./PlanName.jsx";
import stripeLogo from '../../../images/payment/stripe.svg';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const StripeCardModal = ({
    isOpen,
    onClose,
    setupIntentClientSecret,
    project,
    billingAccount,
    selectedPackage,
    selectedSubscription,
    onPaymentSuccess,
}) => {
    const { t } = useTranslation();
    const { getAuthorityConfiguration } = useContext(GlobalContext);
    const stripe = useStripe();
    const elements = useElements();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const checkSetupIntent = async () => {
            try {
                if (!setupIntentClientSecret) {
                    throw new Error("SetupIntent client secret is not provided");
                }

                const { setupIntent } = await stripe.retrieveSetupIntent(setupIntentClientSecret);

                // If SetupIntent is successful, immediately call checkPaymentSetup
                if (setupIntent.status === 'succeeded') {
                    await checkPaymentSetup();
                }
            } catch (error) {
                console.error("Failed to retrieve SetupIntent:", error);
                toast.error("Failed to load payment information. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        if (stripe && setupIntentClientSecret) {
            checkSetupIntent();
        }
    }, [setupIntentClientSecret, stripe]);

    const checkPaymentSetup = async () => {
        try {
            const api = new SubscriptionApi(getAuthorityConfiguration());
            await api.checkSubscriptionPaymentSetup({
                projectId: project.id,
            });
            onPaymentSuccess();
            toast.success('Payment method was set succesfully');
        } catch (error) {
            console.error("Failed to confirm subscription:", error);
            toast.error('Failed to communicate with the server.');
        }
    };

    const handleCardSubmission = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Create payment method using stripe.createPaymentMethod
            const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: elements.getElement(CardNumberElement),
                billing_details: {
                    name: billingAccount ? billingAccount.companyName : project.billingAccount?.companyName,
                },
            });

            if (createError) {
                console.error("Error during createPaymentMethod:", createError);
                toast.error(createError.message || 'An error occurred during payment setup.');
                setIsSubmitting(false);
                return;
            }

            // Confirm payment method using stripe.confirmCardSetup
            const { error: setupError } = await stripe.confirmCardSetup(
                setupIntentClientSecret,
                { payment_method: paymentMethod.id }
            );

            if (setupError) {
                if (setupError.code === 'setup_intent_unexpected_state') {
                    console.log("SetupIntent was already confirmed, proceeding as if successful.");
                } else {
                    toast.error("Payment setup failed: " + setupError.message);
                    setIsSubmitting(false);
                    return;
                }
            }

            // Call the function checkPaymentSetup after confirming payment method
            await checkPaymentSetup();

        } catch (error) {
            console.error("An error occurred in card submission:", error);
            toast.error(error.message || 'An unexpected error occurred during payment setup.');
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    let amountToPay = 0;
    let expiresString = '';

    if (selectedPackage) {
        if (!selectedSubscription.subscription.isFreeTrial) {
            amountToPay = selectedSubscription.subscription.billingCycle === 'annual'
                ? selectedPackage.annualCzk
                : selectedPackage.monthlyCzk;
        }

        if (amountToPay === 0) {
            amountToPay = t('free');
        } else {
            amountToPay = new Intl.NumberFormat('cs-CZ', {
                style: 'currency',
                currency: 'CZK',
            }).format(amountToPay);
        }

        const expires = new Date(selectedSubscription.subscription.endDate);
        expiresString = expires.toLocaleDateString('cs-CZ');
    }

    return ReactDOM.createPortal(
        <div className="modal__billing fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex">
            <div className="relative bg-white dark:bg-gray-800 w-full max-w-lg m-auto flex-col flex rounded-lg shadow-lg">
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700/60">
                    <div className="flex justify-between items-center">
                        <div className="font-semibold text-gray-800 dark:text-gray-100">
                            {t('project.payment-data')}
                        </div>
                        <button
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                            onClick={onClose}
                        >
                            <div className="sr-only">Close</div>
                            <svg className="fill-current" width="16" height="16" viewBox="0 0 16 16">
                                <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z"></path>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="px-5 py-4">
                    {selectedPackage && (
                        <div className="mb-4 w-full h-full text-left py-3 px-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-400 dark:border-gray-700/60 shadow-sm transition">
                            <div className="flex flex-wrap items-center justify-between mb-0.5">
                                <span className="font-semibold text-gray-800 dark:text-gray-100">
                                    <PlanName planName={selectedPackage.name} />
                                </span>
                                <span className="font-medium text-green-600">{amountToPay}</span>
                            </div>
                            <div className="text-xs">
                                {t('subscription.expires')}: {expiresString}
                            </div>
                        </div>
                    )}
                    <form onSubmit={handleCardSubmission} className="bg-white border border-gray-300 shadow-lg rounded-lg p-6 pb-3">
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">{t('subscription.card-number')}</label>
                            <div className="border border-gray-300 rounded p-2 bg-gray-100 focus:ring-indigo-500 focus:border-indigo-500">
                                <CardNumberElement
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: '16px',
                                                color: '#32325d',
                                                '::placeholder': {
                                                    color: '#aab7c4',
                                                },
                                            },
                                            invalid: {
                                                color: '#fa755a',
                                            },
                                        },
                                        showIcon: true,
                                    }}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    {t('subscription.expiration-date')}
                                </label>
                                <div className="border border-gray-300 rounded p-2 bg-gray-100 focus:ring-indigo-500 focus:border-indigo-500">
                                    <CardExpiryElement
                                        options={{
                                            style: {
                                                base: {
                                                    fontSize: '16px',
                                                    color: '#32325d',
                                                    '::placeholder': {
                                                        color: '#aab7c4',
                                                    },
                                                },
                                                invalid: {
                                                    color: '#fa755a',
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">CVC</label>
                                <div className="border border-gray-300 rounded p-2 bg-gray-100 focus:ring-indigo-500 focus:border-indigo-500">
                                    <CardCvcElement
                                        options={{
                                            style: {
                                                base: {
                                                    fontSize: '16px',
                                                    color: '#32325d',
                                                    '::placeholder': {
                                                        color: '#aab7c4',
                                                    },
                                                },
                                                invalid: {
                                                    color: '#fa755a',
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <button className={`btn bg-indigo-500 text-white w-full ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`} type="submit" disabled={isSubmitting}>
                            {isSubmitting ? t('subscription.saving-payment-details') : t('subscription.save-payment-details')}
                        </button>

                        <div className="mt-3 text-right">
                            <a href="https://www.stripe.com" target="_blank" rel="noopener noreferrer">
                                <img src={stripeLogo} width="100" alt="Powered by Stripe" className="max-w-full inline" />
                            </a>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
};

const StripeCardModalWithElements = (props) => (
    <Elements stripe={stripePromise}>
        <StripeCardModal {...props} />
    </Elements>
);

export default StripeCardModalWithElements;